/**
 * PATH: packages/email-hub/src/feature/components/email-context/EmailContextChips.tsx
 * FILE_ID: EMAILHUB-EMAILCONTEXT-CHIPS-001
 * PURPOSE: Reach "Synced from" and "Other emails on this job" from the header.
 * WHY: Beside a wide reader those two sections live in the context rail. When
 *      the rail stacks (tablet width and below) they land BELOW the message,
 *      which is where an operator never looks and where they push the AI
 *      section past the bottom chrome. As chips in the identity band they are
 *      one press away and cost the header no extra row.
 * FLOW:
 * ┌──────────────┐   ┌──────────────┐   ┌──────────────────────────────┐
 * │ email + job   │ → │ chip pressed │ → │ AdaptiveOverlay(real section) │
 * └──────────────┘   └──────────────┘   └──────────────────────────────┘
 * DEPENDENCIES:
 * - @quotech/ui AdaptiveOverlay: portal, viewport clamp, flip, outside-press
 *   dismissal, Escape, focus return — none of that is re-implemented here.
 * RELATED:
 * - ./SyncedFromDetails.tsx, ./JobSiblingEmailList.tsx: the popover bodies,
 *   the SAME components the rail renders.
 * - ../EmailJobContextRail.tsx: drops both sections while these chips show.
 */
import { type ReactNode, useRef, useState } from 'react'
import { Mail, User } from 'lucide-react'

import { AdaptiveOverlay } from '@quotech/ui'
import { cn } from '../../../_internal/lib/utils'
import type { Email } from '../../hooks/useEmails'
import { resolveSyncedMailbox } from './emailContextFormat'
import { JobSiblingEmailList } from './JobSiblingEmailList'
import { SyncedFromDetails } from './SyncedFromDetails'
import { useJobSiblingEmails } from './useJobSiblingEmails'

/** Narrower than the lane popovers: both bodies are single-column lists. */
const CONTEXT_POPOVER_WIDTH = 300

interface ContextChipProps {
  id: 'synced-from' | 'other-emails'
  icon: ReactNode
  label: string
  count?: number
  /** Accessible name for the panel; also its heading. */
  panelLabel: string
  children: (close: () => void) => ReactNode
}

/**
 * One chip plus its anchored panel.
 *
 * Inputs: the chip's identity and a render function for the body (it receives
 * `close` so a row that navigates can dismiss the panel it came from).
 * Output: a button in the header flow and, while open, a portalled panel.
 * Failure mode: none of its own — callers decide whether there is anything to
 * show and simply do not render the chip when there is not.
 */
function ContextChip({
  id,
  icon,
  label,
  count,
  panelLabel,
  children,
}: ContextChipProps) {
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        data-testid={`email-context-chip-${id}`}
        data-chip-open={open ? 'true' : 'false'}
        aria-expanded={open}
        aria-haspopup="dialog"
        // The visible word is dropped on a phone (below), so the name has to
        // come from here or the chip becomes an unlabelled icon. It repeats the
        // visible text rather than the panel's heading so the two agree at the
        // widths where both are present (WCAG 2.5.3 label-in-name).
        aria-label={count === undefined ? label : `${label}, ${count}`}
        title={panelLabel}
        className={cn(
          // Text-scale, not touch-scale: this sits on the identity meta line,
          // whose height is part of the header's vertical contract (identity
          // band = 56px inside BODY_FIRST_BUDGET.openResolvedMax). A 44px pill
          // here would push the band past it, so the coarse-pointer target is
          // grown by a pseudo-element instead — the same trick the band's own
          // recipient-details toggle uses.
          'relative inline-flex shrink-0 items-center gap-1 rounded-full border border-border/70',
          'bg-muted/40 px-1.5 py-0.5 text-[10px] font-medium leading-none text-muted-foreground',
          'hover:bg-accent hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-1',
          "before:absolute before:inset-0 before:content-[''] pointer-coarse:before:inset-[-10px]",
          open && 'bg-accent text-foreground',
        )}
      >
        {icon}
        {/* Labels stay visible: the identity band wraps this cluster onto
            its own row below 640px, so the words no longer steal width from
            the sender/date line (BUG-2026-08-14-IDENTITY-ROW-SPILLS-ONTO-THE-CHIPS). */}
        <span className="whitespace-nowrap">{label}</span>
        {count !== undefined && (
          <span className="shrink-0 font-semibold text-foreground/70">
            {count}
          </span>
        )}
      </button>
      <AdaptiveOverlay
        open={open}
        onOpenChange={setOpen}
        anchorRef={anchorRef}
        label={panelLabel}
        testId={`email-context-panel-${id}`}
        popoverWidth={CONTEXT_POPOVER_WIDTH}
      >
        <p className="mb-2 flex items-center gap-1.5 px-1 text-[10px] uppercase tracking-wide text-muted-foreground">
          {icon} {panelLabel}
        </p>
        {children(() => setOpen(false))}
      </AdaptiveOverlay>
    </>
  )
}

export interface EmailContextChipsProps {
  email: Email
  /** Swap the reader to a sibling email (the rail's onJumpTo). */
  onJumpTo: (siblingId: string) => void
}

/**
 * Render whichever related-email chips this email actually has.
 *
 * Inputs: the open email and the reader's jump-to callback. Output: zero, one
 * or two chips — an email with no mailbox attribution and no job link renders
 * nothing at all, so the header is unchanged for those.
 */
export function EmailContextChips({ email, onJumpTo }: EmailContextChipsProps) {
  const mailbox = resolveSyncedMailbox(email)
  const { jobId, siblings, isPending } = useJobSiblingEmails(email)

  if (!mailbox && jobId == null) return null

  return (
    <span
      className="flex shrink-0 items-center gap-1"
      data-testid="email-context-chips"
    >
      {mailbox && (
        <ContextChip
          id="synced-from"
          icon={<User className="h-2.5 w-2.5 shrink-0" aria-hidden />}
          label="Synced From"
          panelLabel="Synced from"
        >
          {() => <SyncedFromDetails mailbox={mailbox} />}
        </ContextChip>
      )}
      {jobId != null && (
        <ContextChip
          id="other-emails"
          icon={<Mail className="h-2.5 w-2.5 shrink-0" aria-hidden />}
          label="Other Emails"
          count={siblings.length}
          panelLabel="Other emails on this job"
        >
          {(close) => (
            <JobSiblingEmailList
              siblings={siblings}
              isPending={isPending}
              onJumpTo={(siblingId) => {
                // The panel is anchored to a chip that belongs to the email
                // being replaced, so leaving it open would point at a stale
                // anchor and re-render its list underneath the operator.
                close()
                onJumpTo(siblingId)
              }}
            />
          )}
        </ContextChip>
      )}
    </span>
  )
}
